import React from 'react';
import styled, { breakpoints, SC } from '../../../styles/theme';
import { IFilterRule, IGeoLocation } from '../../../redux/filters/filter.duck.interface';
import FilterBoxTemplate from './FilterBoxTemplate';
import { rem } from 'polished';
import { isBrandAC, isBrandAP, isBrandDS, isBrandOV } from '../../../constants/main';
import { StylesAC } from './Styles.AC';
import { StylesAP } from './Styles.AP';
import { StylesDS } from './Styles.DS';
import { StylesOV } from './Styles.OV';

export interface FilterBoxProps extends SC {
    isHomepage: boolean;
    onGeoLocationChange?: (geoLocation: IGeoLocation, distanceRadius: number) => void;
}

export const FilterBox = styled(FilterBoxTemplate)`
    position: relative;
    padding: ${rem(10)} ${rem(18)};
    margin: 0 0 ${rem(16)} 0;

    @media (min-width: ${breakpoints.md}px) {
        padding: ${rem(16)} ${rem(80)} ${rem(16)} ${rem(24)};
        background: ${({ theme }) => theme.colors.white};
        ${({ isHomepage }) => (isHomepage ? '' : `margin-bottom: ${rem(70)} !important;`)}
    }

    @media (max-width: ${breakpoints.md}px) {
        padding: ${rem(8)} ${rem(16)} ${rem(8)};
        margin: 0;

        &.filters-opened {
            border-bottom: 4px solid ${({ theme }) => theme.colors.primary};
            margin-bottom: ${rem(58)};
            padding-bottom: ${rem(40)};
        }
    }

    h3 {
        text-transform: uppercase;
        font-size: ${rem('11px')};
    }

    .geolocation-filter-label {
        white-space: nowrap;
        font-size: ${rem(13)};
        font-weight: bold;
        display: block;
        margin-top: ${rem(90)};
        text-transform: uppercase;
        display: none;

        @media (min-width: ${breakpoints.md}px) {
            display: block;
        }
    }

    .reset-filters--button {
        background: none;
        border: none;
        font-family: ${({ theme }) => theme.fonts.fontSecondary};
        color: ${({ theme }) => theme.colors.primary};
        font-size: ${({ theme }) => theme.fontSizes.textBase};
        text-transform: uppercase;
        cursor: pointer;
        padding: 0;
        position: absolute;
        right: ${rem(32)};
        bottom: ${rem(10)};

        @media (min-width: ${breakpoints.md}px) {
            bottom: ${rem(32)};
        }

        &.hidden {
            display: none;
        }
        
        &:focus {
            outline: 1px dotted ${({ theme }) => theme.colors.accessibilityOutlinecolor};
            outline-offset: 3px;
        }

        &:hover {
            color: ${({ theme }) => theme.colors.primary};
        }
    }

    .toggle-filters {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        background-color: ${({ theme }) => theme.colors.black3};
        color: ${({ theme }) => theme.colors.white};
        border: 0;
        width: 100%;
        padding: ${rem(12)} ${rem(16)};
        text-transform: uppercase;

        span {
            display: flex;
            align-items: center;

            > div {
                margin-right: ${rem(12)};
            }
        }

        @media (min-width: ${breakpoints.md}px) {
            display: none;
        }
    }

    .budgetWrapper {
        flex-basis: 100%;
        display: block;

        @media (min-width: ${breakpoints.md}px) {
            padding-right: ${rem(20)};
        }

        .budget {
            margin: ${rem(16)} 0;
        }
    }

    .filterbox-body {
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: space-between;
        @media (min-width: ${breakpoints.md}px) {
            display: grid;
            grid-template-columns: 0.4fr 24.8em 23.1em 1fr;
            > div:nth-child(n) {
                flex-basis: 20%;
                margin-top: 0;
                max-width: ${rem(300)};
            }

            > div.budgetWrapper {
                flex-basis: 10%;
                margin-top: 0;
            }

            > div:nth-child(2) {
                flex-basis: 15%;
                margin-top: 0;
                max-width: ${rem(220)};
            }
        }

        @media (min-width: ${breakpoints.md}px) {
            flex-direction: row;
        }

        .Select__control {
            border: none;
        }
    }

    .disclaimer {
        font-size: ${({ theme }) => theme.fontSizes.h5};
    }

    .frontpage-legaltext {
        text-align: left;
        color: ${({ theme }) => theme.colors.grey5};
        margin-bottom: 0;
        font-size: ${({ theme }) => theme.fontSizes.textTiny};
        margin-top: ${rem('20px')};
        @media (min-width: ${breakpoints.md}px) {
            padding-right: ${rem(120)};
        }
    }

    .frontpage-legaltext-over.disabled {
        @media (max-width: ${breakpoints.md}px) {
            display: none;
        }
    }

    .filterbox-collapse-button {
        border: 0;
        height: ${rem(35)};
        width: ${rem(230)};
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        background-color: ${({ theme }) => theme.colors.secondary};
        color: ${({ theme }) => theme.colors.white};
        display: flex;
        justify-content: space-around;
        align-items: center;
        font-size: ${({ theme }) => theme.fontSizes.textBase};
        text-transform: uppercase;
        margin-top: ${rem(2)};
        border-bottom-left-radius: ${rem(5)};
        border-bottom-right-radius: ${rem(5)};

        @media (max-width: ${breakpoints.md}px) {
            display: none;
        }

        > div {
            display: flex;
        }

        svg {
            transform: rotate(180deg);
        }

        &:hover {
            cursor: pointer;
        }

        &.collapsed {
            svg {
                transform: rotate(0deg);
            }
        }

        .icon-wrapper {
            height: ${rem(14)};
        }
    }

    .filterbox-extra-filters {
        margin-top: ${rem(20)};
        display: none;
        flex-direction: column;
        justify-content: space-between;

        .MultiSelect-container {
            display: flex;
            width: 100%;
            max-width: ${rem('720px')};
            justify-content: space-between;
            flex-wrap: wrap;
            margin-left: ${rem('2px')};
            margin-right: ${rem('20px')};

            @media (max-width: ${breakpoints.md}px) {
                flex-direction: column;
                margin-left: unset;
            }
        }

        &.visible {
            display: flex;
            padding-bottom: ${rem(16)};
            @media (max-width: ${breakpoints.md}px) {
                margin-top: unset;
            }
            @media (min-width: ${breakpoints.lg}px) {
                grid-template-columns: 36.6em 1.3fr;
            }
            @media (min-width: ${breakpoints.md}px) {
                display: grid;
                grid-template-columns: 0.9fr 1.3fr;
            }
        }

        .filter {
            margin-top: ${rem(24)};

            .filter-title {
                margin-bottom: ${rem('15px')};
            }

            &:not(.color-filter) {
                > div {
                    border: solid 1px ${({ theme }) => theme.colors.secondary};
                    padding: ${rem(4)} ${rem(10)} ${rem(4)} ${rem(20)};
                }
            }

            @media (min-width: ${breakpoints.md}px) {
                max-width: ${rem(350)};
                margin-top: 0;
                flex: 1 0 20%;

                &.color-filter {
                    max-width: ${rem(525)};
                }
            }
        }

        .color-filter {
            &__content {
                display: flex;
                margin-top: ${rem(5)};
            }
        }
    }

    .geolocation-input-container {
        margin-top: ${rem(16)};

        @media (min-width: ${breakpoints.md}px) {
            margin-top: ${rem(50)};
            margin-right: ${rem(50)};
        }

        @media (min-width: ${breakpoints.lg}px) {
            max-width: none;
        }
    }

    .price-mobile-finance-range {
        display: flex;
        align-items: center;

        .mobile-finance-toggle {
            margin-right: ${rem(32)};
            margin-bottom: ${rem(16)};
        }
    }

    .price-geoLocation-container {
        width: 100%;
        margin-top: ${rem(16)};
        position: relative;
        max-width: ${rem(420)};

        @media (min-width: ${breakpoints.md}px) {
            margin-top: ${rem(45)};
        }

        @media (max-width: ${breakpoints.md}px) {
            max-width: unset;
        }
    }

    .mobile-filter-title {
        display: flex;

        .reset-filters--button {
            padding: 0;
            margin-top: 0;
            right: 0;
            min-width: unset;
            min-height: unset;
            position: relative;
        }
    }

    .mobile-filter-vehicles-count {
        font-size: ${rem(18)};
        font-weight: ${({ theme }) => theme.fontWeights.textMedium};
        line-height: 1.25;
        width: 100%;
        padding-bottom: ${rem(8)};
        margin-top: ${rem(24)};
        border-bottom: solid 1px ${({ theme }) => theme.colors.grey3};
        text-align: center;
        text-transform: unset;

        &.hidden {
            display: none;
        }
    }

    .filters-title-badge {
        font-size: ${rem(12)};
        border-radius: ${rem('3px')};
        margin-left: ${rem(12)};
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.red1};
        width: ${rem(32)};
        height: ${rem(18)};
        justify-content: center;
    }

    .mobile-filter-close-button {
        position: absolute;
        bottom: ${rem('-40px')};
        border: 0;
        left: 50%;
        transform: translateX(-50%);
        background-color: ${({ theme }) => theme.colors.grey13};
        color: ${({ theme }) => theme.colors.white};
        width: ${rem('260px')};
        height: ${rem('40px')};
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0 0 ${rem(8)} ${rem(8)};
        cursor: pointer;
        border-top: 4px solid ${({ theme }) => theme.colors.primary};
        text-transform: uppercase;
        font-size: ${rem(11)};
        font-weight: bold;

        span {
            position: relative;
            top: -3px;
            left: 12px;
        }

        &.hidden {
            display: none;
        }
    }

    .show-if-mobile {
        @media (min-width: ${breakpoints.md}px) {
            display: none;
        }
    }

    .show-if-desktop {
        @media (max-width: ${breakpoints.md}px) {
            display: none;
        }
    }

    /* grid fix on buttons */
    .filter-buttons-wrapper {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: ${rem(20)} ${rem(45)} ${rem(45)};
        grid-gap: ${rem(15)} ${rem(5)};
        position: relative;
        box-sizing: border-box;
        flex: 1 0;
        padding: 0;
        margin-top: ${rem(24)};

        @media (min-width: ${breakpoints.md}px) {
            margin-top: 0;
            margin-right: ${rem(15)};
            grid-template-columns: repeat(2, minmax(${rem(100)}, ${rem(180)}));
            margin-right: ${rem(20)};
            max-width: 21rem;

            &--price-range {
                max-width: 50rem !important;
            }
        }

        @media (max-width: ${breakpoints.md}px) {
            grid-template-rows: unset;
        }

        &--price-range {
            grid-template-rows: auto;
            grid-template-columns: 1fr;
        }

        .slider-over {
            @media (min-width: ${breakpoints.md}px) {
                margin-left: -${rem(120)};
                top: -${rem(6)};
                width: 125%;
                position: absolute;
            }
            @media (min-width: ${breakpoints.lg}px) {
                margin-left: -${rem(160)};
            }
            margin-top: 0;

            .range-display {
                font-size: ${({ theme }) => theme.fontSizes.textSmall};
            }
        }

        .filter-title {
            grid-column: 1/3;
            width: 100%;
        }

        & > button:nth-child(2) {
            max-width: unset;
        }

        .stock-generated-filter-button {
            min-width: unset;
            max-width: unset;
            max-height: unset;
            margin: 0;
            padding: 0 ${rem(15)};
        }

        &.onMobile-hidden {
            display: none;

            @media (min-width: ${breakpoints.md}px) {
                display: grid;
            }
        }

        .disp-inline {
            display: flex;
            align-items: center;
            justify-content: left;
            text-align: left;
        }
    }

    ${isBrandAC && StylesAC};
    ${isBrandAP && StylesAP};
    ${isBrandDS && StylesDS};
    ${isBrandOV && StylesOV};
`;

export default FilterBox;
